import React, { useEffect, useReducer, useState } from "react";
import DashboardContainer from "../../components/dashboard-container";
import { Box, Typography } from "@mui/material";
import { roleSidemenuMapService } from "../../utils/services/role-sidemenu-map.service";
import RoleSidemenuList from "../../components/role-sidemenu-list";
import RoleSidemenuModal from "../../components/role-sidemenu-modal";
import AutocompleteWithPagination from "../../components/autocomplete-with-pagination";
import { roleService } from "../../utils/services/role.service";
import CustomPagination from "../../components/pagination";
import { filterReducer } from "../../utils/filterConfig";
import { roleSidemenuFilterConfig } from "../../utils/filter-config";
import buildQuery from "../../utils/query-builder";

const RoleSidemenu = () => {
  const [filterData, dispatch] = useReducer(
    filterReducer,
    roleSidemenuFilterConfig,
  );
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [page, setPage] = useState(0);
  const [dataCount, setDataCount] = useState(0);

  const fetchMappings = async (roleId) => {
    if (!roleId) {
      setMappings([]);
      setDataCount(0);
      return;
    }

    setLoading(true);
    try {
      const query = buildQuery(filterData, page);
      const response = await roleSidemenuMapService.fetchByRolePaginated(
        roleId,
        query,
      );
      setMappings(
        (response.data || []).sort(
          (a, b) => (a.sidemenu?.order || 0) - (b.sidemenu?.order || 0),
        ),
      );
      setDataCount(response?.total || 0);
    } catch (error) {
      console.error("Fetch failed:", error);
      setMappings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMappings(selectedRole?._id);
  }, [selectedRole, page, filterData]);

  const handleRoleChange = (role) => {
    setSelectedRole(role);
    setPage(0);
  };

  const handleEditMapping = (mapping) => {
    setSelectedMapping(mapping);
    setOpenModal(true);
  };

  const handleDeleteMapping = async (id) => {
    try {
      await roleSidemenuMapService.delete(id);
      fetchMappings(selectedRole?._id);
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  const handleAddData = () => {
    fetchMappings(selectedRole?._id);
  };

  const handleToggleAccessible = async (mapping, accessible) => {
    try {
      await roleSidemenuMapService.edit(mapping._id, { accessible });
      fetchMappings(selectedRole?._id);
    } catch (error) {
      console.error("Toggle accessible failed:", error);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedMapping(null);
  };

  const handleFilterChange = (newFilter) => {
    dispatch({ type: "SET_FILTER", payload: newFilter });
    setPage(0);
  };

  return (
    <>
      <DashboardContainer
        filterButton={false}
        hideUploadButton
        header={"Role-Sidemenu Management"}
        title={"+ Mapping"}
        onClickAddButton={() => setOpenModal(true)}
        setViewMode={() => {}}
        hideFilterClient={true}
        onClickRefresh={() => fetchMappings(selectedRole?._id)}
        headerButtons={
          <AutocompleteWithPagination
            selectedItem={selectedRole}
            customLabel="Role"
            onDataChange={handleRoleChange}
            width={250}
            optionKey={(role) => role.name}
            apiEndPoint={(limit, skip, search = "") =>
              roleService.fetch(limit, skip, search)
            }
          />
        }
      >
        {selectedRole ? (
          <>
            <RoleSidemenuList
              data={mappings}
              onEditMapping={handleEditMapping}
              onDeleteMapping={handleDeleteMapping}
              onToggleAccessible={handleToggleAccessible}
              loading={loading}
              filterData={filterData}
              onChangeFilter={handleFilterChange}
              currentPage={page}
              itemsPerPage={filterData.limit}
            />
            <CustomPagination
              dataCount={dataCount}
              rowsPerPage={filterData.limit}
              page={page}
              setPage={setPage}
              setRowsPerPage={(e) =>
                dispatch({ type: "UPDATE_LIMIT", payload: e })
              }
              disableNext={dataCount <= (page + 1) * filterData.limit}
            />
          </>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="400px"
            textAlign="center"
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Please select a role to view sidemenu mappings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a role from the dropdown above to see and manage its
              sidemenu mappings.
            </Typography>
          </Box>
        )}
      </DashboardContainer>

      <RoleSidemenuModal
        open={openModal}
        handleClose={handleCloseModal}
        onAddData={handleAddData}
        selectedMapping={selectedMapping}
      />
    </>
  );
};

export default RoleSidemenu;
